import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import CCModal from 'components/shared/DesignSystem/CCModal/CCModal';
import CCCheckbox from 'components/shared/DesignSystem/SlabTransferPortal/CCCheckbox';
import Text from 'components/shared/DesignSystem/SlabTransferPortal/Text';

// #region styled-components
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_xl);
`;

const ImageLayout = styled.div`
  flex: 0 0 140px;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const Content = styled.div`
  flex: 1;
`;

const StyledCheckbox = styled(CCCheckbox)`
  display: 'flex';
  margin: var(--cc_size_spacing_m) 0 var(--cc_size_spacing_m) 0;
`;
// #endregion

const messages = defineMessages({
  contentLabel: {
    id: 'Core.confirmationModal.contentLabel',
    defaultMessage: 'Confirmation',
    description: 'Accessibility label for modal',
  },
});

const ConfirmationModal = ({
  title,
  subtitle = null,
  message,
  image,
  confirmAction,
  confirmLabel,
  cancelLabel,
  onRequestClose,
  destructive = true, // Shows blue confirm button (instead of red) if false
  // These three props are for the checkbox at the bottom of the body
  secondaryConfirmationLabel,
  useSecondaryConfirmation,
  secondaryConfirmationDefault,
  // These props are for the checkbox in the footer
  showCheckbox = false,
  checkboxText,
  checkboxDefault = false,
}) => {
  const intl = useIntl();
  const [processing, setProcessing] = useState(false);
  const [bodyCheckboxChecked, setBodyCheckboxChecked] = useState(
    secondaryConfirmationDefault,
  );
  const [footerCheckboxChecked, setFooterCheckboxChecked] =
    useState(checkboxDefault);
  const setProcessingAndRun = (fun, e) => {
    if (!processing) {
      setProcessing(true);
      if (useSecondaryConfirmation) {
        fun(e, bodyCheckboxChecked);
      } else {
        // not a typo (TODO: refactor and harmonize the checkbox thing)
        fun(e, footerCheckboxChecked);
      }
    }
  };

  const confirm = () => {
    setBodyCheckboxChecked(!bodyCheckboxChecked);
  };

  const secondaryConfirmation = () => {
    return (
      <StyledCheckbox
        id="secondary-confirmation"
        label={
          <Text as="p" family="system" color="subtle" size="xs">
            {secondaryConfirmationLabel}
          </Text>
        }
        size={14}
        onChange={confirm}
        checked={bodyCheckboxChecked}
        checkedBackgroundColor="var(--cc_color_button_background_primary)"
        checkmarkColor="var(--cc_color_button_text_primary)"
      />
    );
  };

  return (
    <CCModal
      isOpen
      onRequestClose={onRequestClose}
      title={title}
      label={intl.formatMessage(messages.contentLabel)}
      primaryAction={{
        isDelete: destructive,
        label: confirmLabel,
        onClick: (e) => {
          setProcessingAndRun(confirmAction, e);
        },
        disabled: processing,
        'data-testid': 'confirmation-modal__accept',
      }}
      secondaryAction={{
        label: cancelLabel,
        onClick: onRequestClose,
        disabled: false,
        'data-testid': 'confirmation-modal__cancel',
      }}
      footerContentBefore={
        showCheckbox ? (
          <CCCheckbox
            id="confirmation-modal__dont-show-again"
            label={checkboxText}
            checked={footerCheckboxChecked}
            onChange={(e) => {
              setFooterCheckboxChecked(e.target.checked);
            }}
          />
        ) : null
      }
      testId="confirmation-modal__content"
    >
      <Wrapper>
        {image ? (
          <ImageLayout>
            <Image src={image} alt="" />
          </ImageLayout>
        ) : null}
        <Content>
          {subtitle ? (
            <Text size="m" family="heading" weight={700} ccMargin="none none s">
              {subtitle}
            </Text>
          ) : null}
          <Text>{message}</Text>
          {useSecondaryConfirmation ? secondaryConfirmation() : null}
        </Content>
      </Wrapper>
    </CCModal>
  );
};

ConfirmationModal.propTypes = {
  title: PropTypes.node,
  subtitle: PropTypes.node,
  message: PropTypes.node,
  image: PropTypes.node,
  confirmAction: PropTypes.func,
  confirmLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  onRequestClose: PropTypes.func,
  destructive: PropTypes.bool,
  secondaryConfirmationLabel: PropTypes.string,
  useSecondaryConfirmation: PropTypes.bool,
  secondaryConfirmationDefault: PropTypes.bool,
  showCheckbox: PropTypes.bool,
  checkboxText: PropTypes.string,
  checkboxDefault: PropTypes.bool,
};

export default ConfirmationModal;
